import {
  useEffect,
  useId,
  useRef,
  useState,
  Children,
  isValidElement,
  cloneElement,
  type ReactElement,
  type ReactNode,
} from "react";
import { X, Plus, Trash2, RotateCcw, Eye, EyeOff } from "lucide-react";
function PasswordControl({
  input,
  label,
}: {
  input: ReactElement<Record<string, unknown>>;
  label: string;
}) {
  const [visible, setVisible] = useState(false);
  const action = `${visible ? "Hide" : "Show"} ${label.toLowerCase()}`;
  return (
    <span className="password-input">
      {cloneElement(input, { type: visible ? "text" : "password" })}
      <button
        type="button"
        className="password-visibility"
        aria-label={action}
        title={action}
        aria-pressed={visible}
        aria-controls={String(input.props.id)}
        disabled={Boolean(input.props.disabled)}
        onMouseDown={(event) => event.preventDefault()}
        onClick={() => setVisible((value) => !value)}
      >
        {visible ? (
          <EyeOff size={18} aria-hidden="true" />
        ) : (
          <Eye size={18} aria-hidden="true" />
        )}
      </button>
    </span>
  );
}
export function Button({
  children,
  onClick,
  kind = "",
  disabled = false,
  title,
  "aria-label": ariaLabel,
  "aria-current": ariaCurrent,
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  kind?: string;
  disabled?: boolean;
  title?: string;
  "aria-label"?: string;
  "aria-current"?: "page" | "step" | "true";
  type?: "button" | "submit";
}) {
  return (
    <button
      type={type}
      className={`button ${kind}`}
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel}
      aria-current={ariaCurrent}
    >
      {children}
    </button>
  );
}
export function Control({
  label,
  children,
  note,
  reset,
}: {
  label: string;
  children: ReactNode;
  note?: string;
  reset?: () => void;
}) {
  const controlId = useId();
  const labeledChildren = Children.map(children, (child) => {
    if (
      !isValidElement(child) ||
      !["input", "select", "textarea"].includes(String(child.type))
    )
      return child;
    const element = child as ReactElement<Record<string, unknown>>;
    const labeled = cloneElement(element, {
      id: element.props.id || controlId,
      "aria-label": element.props["aria-label"] || label,
      "aria-describedby": note
        ? `${controlId}-note`
        : element.props["aria-describedby"],
    });
    return child.type === "input" && element.props.type === "password" ? (
      <PasswordControl
        input={labeled}
        label={String(labeled.props["aria-label"])}
      />
    ) : (
      labeled
    );
  });
  return (
    <div className="control">
      <span className="control-label">
        <label htmlFor={controlId}>{label}</label>
        {reset && (
          <button
            type="button"
            className="reset-link"
            title={`Inherit ${label} from project`}
            onClick={(e) => {
              e.preventDefault();
              reset();
            }}
          >
            <RotateCcw size={11} />
            Inherit
          </button>
        )}
      </span>
      {labeledChildren}
      {note && (
        <small id={`${controlId}-note`} className="muted">
          {note}
        </small>
      )}
    </div>
  );
}
export function Toggle({
  label,
  value,
  onChange,
  disabled = false,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="toggle">
      <span>{label}</span>
      <input
        type="checkbox"
        role="switch"
        checked={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
    </label>
  );
}
export function Modal({
  title,
  children,
  onClose,
  wide = false,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const backdrop = useRef<HTMLDivElement>(null);
  useEffect(() => {
    backdrop.current
      ?.querySelector<HTMLElement>("input,textarea,select,button")
      ?.focus();
  }, []);
  useEffect(() => {
    const fn = (e: KeyboardEvent) => {
      if (!e.composedPath().includes(backdrop.current!)) return;
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", fn);
    return () => document.removeEventListener("keydown", fn);
  }, [onClose]);
  return (
    <div
      ref={backdrop}
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section
        className={`modal ${wide ? "wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header>
          <h2>{title}</h2>
          <button
            className="icon-button"
            aria-label="Close dialog"
            onClick={onClose}
          >
            <X size={20} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}
export { ConfigurationEditor as JsonEditor } from "./ConfigurationEditor";
export type SetupMode = "guided" | "advanced" | "json";
/**
 * One switch for every setup surface: a guided editor first, the full
 * control-by-control setup second, and raw JSON for exact configuration.
 */
export function SetupModes({
  label,
  value,
  onChange,
  guidedLabel = "Guided setup",
  modes = ["guided", "advanced", "json"],
}: {
  label: string;
  value: SetupMode;
  onChange: (mode: SetupMode) => void;
  guidedLabel?: string;
  modes?: SetupMode[];
}) {
  const names: Record<SetupMode, string> = {
    guided: guidedLabel,
    advanced: "Advanced setup",
    json: "JSON",
  };
  return (
    <div className="setup-modes" role="group" aria-label={label}>
      {modes.map((mode) => (
        <button
          key={mode}
          type="button"
          aria-pressed={value === mode}
          className={value === mode ? "active" : ""}
          onClick={() => onChange(mode)}
        >
          {names[mode]}
        </button>
      ))}
    </div>
  );
}
/** Plain name/value rows for string maps such as PDF metadata. */
export function KeyValueEditor({
  label,
  value,
  onChange,
  keyLabel = "Name",
  valueLabel = "Value",
  addLabel = "Add row",
}: {
  label: string;
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  keyLabel?: string;
  valueLabel?: string;
  addLabel?: string;
}) {
  const rows = Object.entries(value);
  const write = (next: [string, string][]) =>
    onChange(Object.fromEntries(next));
  return (
    <div className="key-value-editor" role="group" aria-label={label}>
      {rows.map(([key, text], index) => (
        <div className="key-value-row" key={index}>
          <input
            aria-label={`${keyLabel} ${index + 1}`}
            placeholder={keyLabel}
            value={key}
            onChange={(e) =>
              write(
                rows.map((row, i) =>
                  i === index ? [e.target.value, row[1]] : row,
                ),
              )
            }
          />
          <input
            aria-label={`${valueLabel} ${index + 1}`}
            placeholder={valueLabel}
            value={text}
            onChange={(e) =>
              write(
                rows.map((row, i) =>
                  i === index ? [row[0], e.target.value] : row,
                ),
              )
            }
          />
          <button
            type="button"
            className="icon-button"
            aria-label={`Remove ${key || `row ${index + 1}`}`}
            onClick={() => write(rows.filter((_, i) => i !== index))}
          >
            <Trash2 size={14} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="text-button"
        disabled={Object.hasOwn(value, "")}
        onClick={() => write([...rows, ["", ""]])}
      >
        <Plus size={13} /> {addLabel}
      </button>
    </div>
  );
}
export function Empty({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <div className="empty-symbol">
        <Plus size={24} />
      </div>
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  );
}
export function Chips({
  items,
  onRemove,
}: {
  items: string[];
  onRemove: (i: number) => void;
}) {
  return (
    <div className="chips">
      {items.map((item, i) => (
        <span className="chip" key={i}>
          {item}
          <button title="Remove" onClick={() => onRemove(i)}>
            <Trash2 size={12} />
          </button>
        </span>
      ))}
    </div>
  );
}
